import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Check, Guitar } from 'lucide-react';
import type { InstrumentId } from '@audigoal/core';
import { Button, Chip, Sheet } from '@/components';
import { useSession } from './session-context';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

/**
 * The active instrument, and one tap to change it.
 *
 * It used to be a chip naming the instrument that opened the tuner — which is
 * a reasonable thing to want and not at all what the label promised — while
 * actually switching meant going to Settings. The path, the studio, every
 * play-along and every microphone exercise all follow this one field, so
 * changing it is something a learner does often and should be able to do from
 * wherever they are.
 *
 * Only the instruments chosen during onboarding are listed: this switches
 * between them, it does not take one up. Adding one is still Settings, and the
 * sheet says so and links there.
 */
export function InstrumentSwitcher() {
  const { profile, updateProfile } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const active = profile.primaryInstrument as InstrumentId;
  const choices = profile.instruments as InstrumentId[];
  const theoryOnly = profile.flags.theoryOnly === true;

  const choose = (instrument: InstrumentId): void => {
    setOpen(false);
    if (instrument === active && !theoryOnly) return;
    // Turning theory-only off is part of picking an instrument: a learner who
    // taps a ukulele means to be shown the ukulele's path.
    void updateProfile({
      primaryInstrument: instrument,
      flags: { ...profile.flags, theoryOnly: false },
    });
  };

  return (
    <>
      <Chip
        tone="neutral"
        onClick={() => setOpen(true)}
        aria-label={t('topbar.switchInstrument', {
          name: theoryOnly ? t('onboarding.theoryOnly') : t(`instrument.${active}`),
        })}
        aria-haspopup="dialog"
        className="max-w-[9rem] truncate"
      >
        <Guitar size={18} aria-hidden />
        <span className="truncate">
          {theoryOnly ? t('onboarding.theoryOnly') : t(`instrument.${active}`)}
        </span>
      </Chip>

      <Sheet open={open} onClose={() => setOpen(false)} title={t('topbar.switchTitle')}>
        <div className="flex flex-col gap-2">
          {choices.map((instrument) => {
            const current = !theoryOnly && instrument === active;
            return (
              <button
                key={instrument}
                type="button"
                onClick={() => choose(instrument)}
                aria-current={current ? 'true' : undefined}
                className={cn(
                  'flex min-h-14 items-center gap-3 rounded-xl border-2 px-3 py-2 text-left',
                  current ? 'border-brand bg-brand/10' : 'border-line bg-surface hover:bg-bg-2',
                )}
              >
                <span className="flex-1 font-bold">{t(`instrument.${instrument}`)}</span>
                {current ? <Check size={20} className="text-brand" aria-hidden /> : null}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void updateProfile({ flags: { ...profile.flags, theoryOnly: true } });
            }}
            aria-current={theoryOnly ? 'true' : undefined}
            className={cn(
              'flex min-h-14 items-center gap-3 rounded-xl border-2 px-3 py-2 text-left',
              theoryOnly ? 'border-brand bg-brand/10' : 'border-line bg-surface hover:bg-bg-2',
            )}
          >
            <span className="flex-1">
              <span className="block font-bold">{t('onboarding.theoryOnly')}</span>
              <span className="block text-sm text-text-2">{t('onboarding.theoryOnlyBody')}</span>
            </span>
            {theoryOnly ? <Check size={20} className="text-brand" aria-hidden /> : null}
          </button>

          <p className="text-sm text-text-2">{t('topbar.switchHint')}</p>
          <div className="flex flex-col gap-2">
            <Button
              block
              variant="neutral"
              onClick={() => {
                setOpen(false);
                void navigate('/tuner');
              }}
            >
              {t('mic.tunerTitle')}
            </Button>
            <Button
              block
              variant="ghost"
              onClick={() => {
                setOpen(false);
                void navigate('/settings');
              }}
            >
              {t('topbar.manageInstruments')}
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  );
}
